
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { CustomDurationDialogValues  } from "../../components/custom-duration-dialog/CustomDurationDialog";
import CustomDurationUtils, { DurationOptionEnum } from "../../components/custom-duration-dialog/CustomDurationUtils";
import { addHours } from "date-fns";
import _ from "lodash";
import { GridData } from "../../components/ag-grid-extensions/models/GridData";
import { alarmNotificationAdminReportActionItems } from "./AlarmNotificationAdminReportActions";
import { ISortParameters } from "../../common/models/ISortParameters";
import { ActionMenuItem } from "../../components/action-menu/ActionMenuItem";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type AlarmNotificationAdminReportStateType = {
    viewOptionState: ViewOptionStateEnum,
    organizationID: number | undefined,
    durationLabel: string,
    durationOptions: CustomDurationDialogValues;
    gridData: GridData | undefined;
    actions: ActionMenuItem[];
    downloadUid: string | undefined;
    run: boolean;
    sortParameters: ISortParameters
};

export const defaultAlarmNotificationAdminReportState: AlarmNotificationAdminReportStateType = {
    viewOptionState: ViewOptionStateEnum.Default,
    organizationID: undefined,
    durationLabel: CustomDurationUtils.getCustomDurationLabel(DurationOptionEnum.OneDay, true),
    durationOptions: {
        option: DurationOptionEnum.OneDay,
        duration: {
            startDate: addHours(new Date(), -24),
            endDate: new Date()
        }
    },
    gridData: undefined,
    actions: [...alarmNotificationAdminReportActionItems],
    downloadUid: undefined,
    run: false,
    sortParameters: {
        propertyName: 'alarmTimestamp',
        sortAscending: true
    }
};

enum AlarmNotificationAdminReportStateActionType {

    OrganizationChanged = 'ALARM_NOTIFICATION_REPORT_ORGANIZATION_CHANGED',
    DurationChanged = 'ALARM_NOTIFICATION_REPORT_DURATION_CHANGED',
    GridDataChanged = 'ALARM_NOTIFICATION_REPORT_GRID_DATA_CHANGED',
    ActionsChanged = 'ALARM_NOTIFICATION_REPORT_ACTIONS_CHANGED',
    DownloadUidChanged = 'ALARM_NOTIFICATION_REPORT_DOWNLOAD_UID_CHANGED',
    RunChanged = 'ALARM_NOTIFICATION_REPORT_RUN_CHANGED',
    SortParametersChanged = 'ALARM_NOTIFICATION_SORT_PARAMS_CHANGED',
    ResetChanged = 'ALARM_NOTIFICATION_REPORT_RESET_CHANGED'
}

type AlarmNotificationAdminReportStateActions = {
    type: AlarmNotificationAdminReportStateActionType.OrganizationChanged;
    payload: number;
} | {
    type: AlarmNotificationAdminReportStateActionType.DurationChanged;
    payload: CustomDurationDialogValues;
} | {
    type: AlarmNotificationAdminReportStateActionType.GridDataChanged;
    payload: GridData;
} | {
    type: AlarmNotificationAdminReportStateActionType.ActionsChanged;
    payload: ActionMenuItem[];
} | {
    type: AlarmNotificationAdminReportStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: AlarmNotificationAdminReportStateActionType.RunChanged;
    payload: boolean;
} | {
    type: AlarmNotificationAdminReportStateActionType.SortParametersChanged;
    payload: ISortParameters;
} | {
    type: AlarmNotificationAdminReportStateActionType.ResetChanged;
    payload: void;
};


const optionsChanged = (newState: AlarmNotificationAdminReportStateType, 
    defaultState: AlarmNotificationAdminReportStateType) => 
    newState.durationOptions.option !== defaultState.durationOptions.option;

function alarmNotificationAdminReportStateReducer(state: AlarmNotificationAdminReportStateType, action: AlarmNotificationAdminReportStateActions): AlarmNotificationAdminReportStateType {

    const copy = _.cloneDeep(state);

    switch (action.type) {
        
        case AlarmNotificationAdminReportStateActionType.OrganizationChanged:
            copy.organizationID = action.payload;
            return copy;

        case AlarmNotificationAdminReportStateActionType.GridDataChanged:
            copy.gridData = action.payload;
            return copy;

        case AlarmNotificationAdminReportStateActionType.RunChanged:
            copy.run = action.payload;
            return copy;
    
        case AlarmNotificationAdminReportStateActionType.DownloadUidChanged:
            copy.downloadUid = action.payload;
            return copy;
    
        case AlarmNotificationAdminReportStateActionType.ActionsChanged:
            copy.actions = action.payload;
            return copy;

        case AlarmNotificationAdminReportStateActionType.DurationChanged:

            copy.durationLabel = CustomDurationUtils.getCustomDurationLabel(action.payload.option, true);
            copy.durationOptions =  {
                option: action.payload.option,
                duration: {
                    startDate: new Date(action.payload.duration.startDate as Date),
                    endDate: new Date(action.payload.duration.endDate as Date)
                }
            };

            copy.viewOptionState = optionsChanged(copy, defaultAlarmNotificationAdminReportState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;

            return copy;

        case AlarmNotificationAdminReportStateActionType.SortParametersChanged:
            copy.sortParameters = action.payload;
            return copy;

    
                
        case AlarmNotificationAdminReportStateActionType.ResetChanged:
            return {...defaultAlarmNotificationAdminReportState};

        default:
            return state;
    }
}

export default function useAlarmNotificationAdminReportState() {

    const [state, dispatch] = useReducer(alarmNotificationAdminReportStateReducer, {...defaultAlarmNotificationAdminReportState});

    const setOrganization = useCallback((value: number) => dispatch({
        type: AlarmNotificationAdminReportStateActionType.OrganizationChanged,
        payload: value
    }), []);

    const setDuration = useCallback((value: CustomDurationDialogValues) => dispatch({
        type: AlarmNotificationAdminReportStateActionType.DurationChanged,
        payload: value
    }), []);

    const setGridData = useCallback((value: GridData) => dispatch({
        type: AlarmNotificationAdminReportStateActionType.GridDataChanged,
        payload: value
    }), []);

    // const setActions = useCallback((value: ActionMenuItem[]) => dispatch({
    //     type: AlarmNotificationAdminReportStateActionType.ActionsChanged,
    //     payload: value
    // }), []);

    const setActions = useCallback((actions: ActionMenuItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: AlarmNotificationAdminReportStateActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: AlarmNotificationAdminReportStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    const setRun = useCallback((value: boolean) => dispatch({
        type: AlarmNotificationAdminReportStateActionType.RunChanged,
        payload: value
    }), []);

    const setSortParameters = useCallback((value: ISortParameters) => dispatch({
        type: AlarmNotificationAdminReportStateActionType.SortParametersChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: AlarmNotificationAdminReportStateActionType.ResetChanged,
        payload: undefined
    }), []);



    return {
        state,
        setOrganization,
        setGridData,
        setActions,
        setDownloadUid,
        setDuration,
        setRun,
        setSortParameters,
        resetState
    };
}

